// Utilities
import { computed, effectScope, inject, shallowRef, toRef } from 'vue';
import { createBreakpoints, getCurrentInstanceName, isNull, isNumber, isObject, mergeDeep, propsFactory } from "../util/index.js";
import { IN_BROWSER, SUPPORTS_MATCH_MEDIA, SUPPORTS_TOUCH } from "../util/globals.js"; // Types
export const breakpoints = ['sm', 'md', 'lg', 'xl', 'xxl']; // no xs

export const DisplaySymbol = Symbol.for('vuetify:display');
const defaultDisplayOptions = {
  mobileBreakpoint: 'lg',
  thresholds: {
    xs: 0,
    sm: 600,
    md: 840,
    lg: 1145,
    xl: 1545,
    xxl: 2138
  }
};
const parseDisplayOptions = (options = defaultDisplayOptions) => {
  return mergeDeep(defaultDisplayOptions, options);
};
function getPlatform(ssr) {
  const userAgent = IN_BROWSER && !ssr ? window.navigator.userAgent : 'ssr';
  function match(regexp) {
    return Boolean(userAgent.match(regexp));
  }
  const android = match(/android/i);
  const ios = match(/iphone|ipad|ipod/i);
  const cordova = match(/cordova/i);
  const electron = match(/electron/i);
  const chrome = match(/chrome/i);
  const edge = match(/edge/i);
  const firefox = match(/firefox/i);
  const opera = match(/opera/i);
  const win = match(/win/i);
  const mac = match(/mac/i);
  const linux = match(/linux/i);
  return {
    android,
    ios,
    cordova,
    electron,
    chrome,
    edge,
    firefox,
    opera,
    win,
    mac,
    linux,
    touch: SUPPORTS_TOUCH,
    ssr: userAgent === 'ssr'
  };
}
export function createDisplay(options, ssr) {
  const {
    thresholds,
    mobileBreakpoint
  } = parseDisplayOptions(options);

  // `ssr: true` is the boolean form; createBreakpoints only accepts a size.
  const ssrSize = isObject(ssr) ? ssr : ssr ? {
    clientWidth: 0,
    clientHeight: 0
  } : undefined;
  const screen = createBreakpoints({
    breakpoints: thresholds,
    mobileBreakpoint,
    ssr: ssrSize
  });
  const platform = shallowRef(getPlatform(ssr));

  // ssr defers the listener until update(), and that call happens after
  // createVuetify's scope has returned. The child scope is still stopped
  // with it, so unmount removes the listener.
  const resizeScope = ssrSize ? effectScope() : undefined;
  function update() {
    if (resizeScope) resizeScope.run(() => screen.update());else screen.update();
    platform.value = getPlatform();
  }
  return {
    xs: screen.xs,
    sm: screen.sm,
    md: screen.md,
    lg: screen.lg,
    xl: screen.xl,
    xxl: screen.xxl,
    smAndUp: screen.smAndUp,
    mdAndUp: screen.mdAndUp,
    lgAndUp: screen.lgAndUp,
    xlAndUp: screen.xlAndUp,
    smAndDown: screen.smAndDown,
    mdAndDown: screen.mdAndDown,
    lgAndDown: screen.lgAndDown,
    xlAndDown: screen.xlAndDown,
    name: screen.name,
    height: screen.height,
    width: screen.width,
    mobile: screen.isMobile,
    mobileBreakpoint: shallowRef(mobileBreakpoint),
    platform,
    thresholds: shallowRef(thresholds),
    ssr: !!ssr,
    update
  };
}
export const makeDisplayProps = propsFactory({
  mobile: {
    type: Boolean,
    default: false
  },
  mobileBreakpoint: [Number, String]
}, 'display');
export function useDisplay(props = {
  mobile: null
}, name = getCurrentInstanceName()) {
  const display = inject(DisplaySymbol);
  if (!display) throw new Error('Could not find Vuetify display injection');
  function isBelow(px, width, matchWithMediaQuery) {
    return matchWithMediaQuery ? !window.matchMedia(`(min-width: ${px}px)`).matches : width < px;
  }
  const mobile = computed(() => {
    // Reactivity triggers, do not remove. matchMedia() does not subscribe.
    const width = display.width.value;
    // Hydration must match the server's width comparison until update()
    const matchWithMediaQuery = SUPPORTS_MATCH_MEDIA && !display.platform.value.ssr;
    if (props.mobile) {
      return true;
    } else if (isNumber(props.mobileBreakpoint)) {
      return isBelow(props.mobileBreakpoint, width, matchWithMediaQuery);
    } else if (props.mobileBreakpoint) {
      return isBelow(display.thresholds.value[props.mobileBreakpoint], width, matchWithMediaQuery);
    } else if (isNull(props.mobile)) {
      return display.mobile.value;
    } else {
      return false;
    }
  });
  const displayClasses = toRef(() => {
    if (!name) return {};
    return {
      [`${name}--mobile`]: mobile.value
    };
  });
  return {
    ...display,
    displayClasses,
    mobile
  };
}
//# sourceMappingURL=display.js.map